import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Fab from '@mui/material/Fab'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import colors from '@/assets/scss/_themes-vars.module.scss'

import { ChatInterface } from '@/features/medconnect-agent/ui/ChatInterface'
import { useElaraPanel } from '@/providers/elara/ElaraPanelContext'
import { useGetAllSessionsQuery } from '@/core/services/medconnect-agent-service'
import { useElaraRealtimeLicenseEnabled } from '@/providers/elara/hooks/useElaraRealtimeLicenseEnabled'

export const ELARA_PANEL_WIDTH = 420

/**
 * FAB button — toggles the ELARA panel.
 * Only rendered when the user has an active elara-realtime licence.
 */
export function ElaraPanelFAB() {
    const panel = useElaraPanel()
    const hasElaraLicense = useElaraRealtimeLicenseEnabled()
    if (!panel || panel.isPanelOpen || !hasElaraLicense) return null

    return (
        <Tooltip title="ELARA AI Assistant (Ctrl+E)" placement="left">
            <Fab
                size="medium"
                onClick={panel.togglePanel}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1300,
                    backgroundColor: colors.primaryMain,
                    color: '#fff',
                    '&:hover': { backgroundColor: colors.primaryDark },
                }}
            >
                <SmartToyIcon />
            </Fab>
        </Tooltip>
    )
}

/**
 * Conversation title bar with session history dropdown.
 * Shows current conversation title (like Cloudflare's "Where do I find my ac... ▾").
 */
function ConversationTitleBar({
    sessionId,
    agentType,
    onSessionSelect,
    onNewChat,
}: {
    sessionId: string | null
    agentType: string
    onSessionSelect: (id: string) => void
    onNewChat: () => void
}) {
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const { data: sessions, isLoading } = useGetAllSessionsQuery(agentType)

    const currentSession = useMemo(
        () => sessions?.find((s) => s.sessionId === sessionId),
        [sessions, sessionId],
    )

    const title = currentSession?.title || t('page.elara.panel.newConversation', { defaultValue: 'New conversation' })

    const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget), [])
    const handleClose = useCallback(() => setAnchorEl(null), [])

    const handleSelect = useCallback(
        (id: string) => {
            onSessionSelect(id)
            handleClose()
        },
        [onSessionSelect, handleClose],
    )

    const handleNewChat = useCallback(() => {
        onNewChat()
        handleClose()
    }, [onNewChat, handleClose])

    const recentSessions = useMemo(
        () => (sessions || []).slice(0, 20),
        [sessions],
    )

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textAlign: 'left',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    flex: 1,
                    minWidth: 0,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
            >
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 500, color: colors.gray800 || '#1a1a1a', flex: 1, minWidth: 0 }}
                >
                    {title}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray500, flexShrink: 0 }} />
            </ButtonBase>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            width: ELARA_PANEL_WIDTH - 16,
                            maxHeight: 360,
                            mt: 0.5,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        },
                    },
                }}
            >
                {/* New conversation at the top */}
                <ListItemButton onClick={handleNewChat} sx={{ py: 1 }}>
                    <AddIcon sx={{ fontSize: 18, mr: 1, color: colors.primaryMain }} />
                    <ListItemText
                        primary={t('page.elara.panel.newConversation', { defaultValue: 'New conversation' })}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500, color: colors.primaryMain }}
                    />
                </ListItemButton>
                <Divider />

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : recentSessions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 2, textAlign: 'center' }}>
                        {t('page.elara.panel.noConversations', { defaultValue: 'No previous conversations' })}
                    </Typography>
                ) : (
                    <List dense sx={{ py: 0, maxHeight: 280, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                        {recentSessions.map((session) => (
                            <ListItemButton
                                key={session.sessionId}
                                selected={session.sessionId === sessionId}
                                onClick={() => handleSelect(session.sessionId)}
                                sx={{ py: 0.75 }}
                            >
                                <ListItemText
                                    primary={session.title || session.firstMessagePreview || 'Untitled'}
                                    primaryTypographyProps={{ fontSize: '0.825rem', noWrap: true }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    )
}

/**
 * ELARA side panel — Cloudflare-style.
 */
export function ElaraPanelContent() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const panel = useElaraPanel()
    const [refreshTick, setRefreshTick] = useState(0)
    if (!panel) return null

    const { agentType, sessionId, setSessionId, closePanel, newChat } = panel

    const handleSessionIdChange = useCallback(
        (id: string) => setSessionId(id),
        [setSessionId],
    )

    const handleRefresh = useCallback(() => setRefreshTick((n) => n + 1), [])

    const handleNewChat = useCallback(() => {
        setRefreshTick(0)
        newChat()
    }, [newChat])

    const handleOpenFullPage = useCallback(() => {
        const target = sessionId ? `/medconnect-agent/${sessionId}` : '/medconnect-agent'
        navigate(target)
        closePanel()
    }, [navigate, closePanel, sessionId])

    return (
        <Stack
            sx={{
                width: ELARA_PANEL_WIDTH,
                minWidth: ELARA_PANEL_WIDTH,
                maxWidth: ELARA_PANEL_WIDTH,
                height: '100vh',
                position: 'sticky',
                top: 0,
                borderLeft: `1px solid ${colors.gray200}`,
                backgroundColor: '#fff',
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            {/* Top bar — conversation title + actions, single row */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    px: 1,
                    minHeight: 46,
                    flexShrink: 0,
                    borderBottom: `1px solid ${colors.gray200}`,
                    gap: 0.5,
                }}
            >
                <ConversationTitleBar
                    sessionId={sessionId}
                    agentType={agentType}
                    onSessionSelect={setSessionId}
                    onNewChat={handleNewChat}
                />
                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <Tooltip title={t('page.elara.panel.refreshHistory', { defaultValue: 'Reload history' })}>
                        <span>
                            <IconButton size="small" onClick={handleRefresh} disabled={!sessionId} sx={{ color: colors.gray500 }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('page.elara.panel.newChat', { defaultValue: 'New conversation' })}>
                        <IconButton size="small" onClick={handleNewChat} sx={{ color: colors.gray500 }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('page.elara.panel.openFullPage', { defaultValue: 'Open in full page' })}>
                        <IconButton size="small" onClick={handleOpenFullPage} sx={{ color: colors.gray500 }}>
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close (Ctrl+E)">
                        <IconButton size="small" onClick={closePanel} sx={{ color: colors.gray500 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Chat area — scroll-isolated */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overscrollBehavior: 'contain',
                }}
            >
                <ChatInterface
                    sessionId={sessionId}
                    onSessionIdChange={handleSessionIdChange}
                    agentType={agentType}
                    compact
                    refreshTick={refreshTick}
                />
            </Box>
        </Stack>
    )
}

/**
 * Legacy export for App.tsx — just renders the FAB.
 */
export function ElaraPanel() {
    return <ElaraPanelFAB />
}
